/**
 * Sudoku engine — the variant-agnostic gameplay core.
 *
 * Owns:
 *  - 9x9 board representation with givens vs. user entries
 *  - per-cell candidate (pencil-mark) sets
 *  - move history for undo (placement, candidate toggle, erase)
 *  - validation (conflict detection by row/col/box, completion check)
 *  - selecting from the curated, technique-graded puzzle bank
 *  - timer + GameSession telemetry
 *
 * This file is variant-agnostic: no JSX, no CSS, no audio. Variants consume
 * `useSudokuEngine` and render against the returned state.
 *
 * The puzzle-shaped half lives in the packages: the digit/tier/`SudokuPuzzle`
 * model and the coordinate math in `@rendever/puzzle-core/sudoku`, the 400
 * graded puzzles in `@rendever/puzzle-content/sudoku`. Both are re-exported
 * below so variants can keep importing them from the engine.
 *
 * Difficulty model:
 *   Tiers are defined by the hardest *technique* a human must use to solve —
 *   not by clue count, which is a weak and often misleading proxy. Puzzles are
 *   generated and graded offline (tools/sudoku-grade); the grader in
 *   puzzle-core owns the technique → tier mapping and puzzle-content's test
 *   suite re-verifies every banked puzzle's label, so a mislabeled puzzle
 *   cannot ship.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { prefersReducedMotion } from '@/juice';
import type { DifficultyEngine, GameSession } from '@rendever/games-sdk';

import { decodedBank } from '@rendever/puzzle-content/sudoku';
import {
  SUDOKU_SIZE,
  peers,
  type CellValue,
  type SudokuDifficulty,
  type SudokuDigit,
  type SudokuPuzzle,
} from '@rendever/puzzle-core/sudoku';
import { dailySeed } from '@rendever/puzzle-core/seed';

// Re-exported so variants and tests keep one import for the whole sudoku
// surface, while the packages stay the single definition.
export { idx, peers, rc } from '@rendever/puzzle-core/sudoku';
export type {
  BankedPuzzle,
  CellValue,
  SudokuDifficulty,
  SudokuDigit,
  SudokuPuzzle,
} from '@rendever/puzzle-core/sudoku';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SudokuCell {
  /** The user-visible value (0 = empty). */
  value: CellValue;
  /** True if this cell was supplied by the puzzle and cannot be changed. */
  given: boolean;
  /** Candidate (pencil-mark) digits. Empty when `value > 0`. */
  candidates: ReadonlySet<SudokuDigit>;
  /** True when this cell's value conflicts with another cell. */
  conflict: boolean;
  /**
   * Last time this cell was erased (ms epoch). The renderer uses this to drive
   * the eraser-smudge animation. 0 when never erased.
   */
  lastErasedAt: number;
  /**
   * Small per-cell random tilt baked at placement time (degrees). The
   * pencil-on-paper aesthetic — every number is slightly off-axis. 0 for
   * givens (printed) and empty cells.
   */
  tiltDeg: number;
}

export type SudokuBoard = ReadonlyArray<SudokuCell>; // 81 entries, row-major

export type SudokuPhase = 'intro' | 'play' | 'solved';

export type SudokuMode = 'daily' | 'practice';

type MoveKind = 'place' | 'erase' | 'candidate-add' | 'candidate-remove';

interface Move {
  kind: MoveKind;
  index: number;
  /** For `place`: previous value. For `erase`: erased value. For candidate ops: the digit. */
  digit: SudokuDigit;
  /** For `place`: the cell's prior candidate set (so undo can restore). */
  priorCandidates?: ReadonlySet<SudokuDigit>;
  /** For `place`: the cell's prior value (when overwriting an existing entry). */
  priorValue?: SudokuDigit;
}

export interface SudokuEngineConfig {
  session: GameSession;
  difficulty: DifficultyEngine;
  initialDifficulty?: SudokuDifficulty;
  mode?: SudokuMode;
  /** Test-only clock injection for daily puzzle selection. */
  now?: Date;
}

export interface SudokuEngineState {
  phase: SudokuPhase;
  difficulty: SudokuDifficulty;
  /** Active puzzle. Always defined once `start()` runs. */
  puzzle: SudokuPuzzle;
  /** Live board (81 cells). */
  board: SudokuBoard;
  /** Selected cell index, or null. */
  selected: number | null;
  /** True = next number-button tap toggles a candidate; false = places. */
  pencilMode: boolean;
  /** Move history depth — when 0, undo is unavailable. */
  undoAvailable: boolean;
  /** Number of cells filled in correctly (excludes wrong + empty). */
  correctCount: number;
  /** Cells remaining to fill (regardless of correctness). */
  emptyCount: number;
  /** Total elapsed seconds since `start()`. */
  elapsedSec: number;
  /** Total hints consumed in the current puzzle. */
  hintsUsed: number;
  /** Mistakes count (placements that conflicted at the moment of placement). */
  mistakeCount: number;
}

export interface SudokuEngineActions {
  start: (difficulty?: SudokuDifficulty) => void;
  selectCell: (index: number | null) => void;
  togglePencilMode: () => void;
  placeDigit: (digit: SudokuDigit) => void;
  erase: () => void;
  undo: () => void;
  hint: () => void;
  /** Reset the current puzzle back to its givens (no new puzzle). */
  resetPuzzle: () => void;
  /** Pick another puzzle (same difficulty). */
  nextPuzzle: (modeOverride?: SudokuMode) => void;
  /** Pick puzzle at a specific difficulty. */
  pickDifficulty: (difficulty: SudokuDifficulty) => void;
  /** Dev-only: instantly fill the board with the solution (for screenshots). */
  solveNow: () => void;
  end: () => Promise<void>;
}

export interface SudokuEngineHandle {
  state: SudokuEngineState;
  actions: SudokuEngineActions;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SIZE = SUDOKU_SIZE;

// ---------------------------------------------------------------------------
// Puzzle bank — curated + technique-graded
// ---------------------------------------------------------------------------
//
// The bank ships pre-built and pre-graded in `@rendever/puzzle-content`; the
// engine only picks from it. No runtime solving or carving — difficulty is
// guaranteed at build time by the grader, not approximated by clue count at
// play time.

/**
 * Public accessor — variants can call this to render preview/teasers before
 * the engine actually starts.
 */
export function getPuzzleBank(): Record<SudokuDifficulty, ReadonlyArray<SudokuPuzzle>> {
  return decodedBank();
}

export function dailySudokuSeed(difficulty: SudokuDifficulty, now: Date = new Date()): number {
  return dailySeed(`sudoku:${difficulty}`, now);
}

export function dailySudokuPuzzleIndex(
  difficulty: SudokuDifficulty,
  bankLength: number,
  now: Date = new Date(),
): number {
  if (!Number.isInteger(bankLength) || bankLength <= 0) {
    throw new RangeError(`bankLength must be a positive integer, got ${bankLength}`);
  }
  return dailySudokuSeed(difficulty, now) % bankLength;
}

function pickInitialPuzzleIndex(
  bank: Record<SudokuDifficulty, ReadonlyArray<SudokuPuzzle>>,
  difficulty: SudokuDifficulty,
  mode: SudokuMode,
  now: Date | undefined,
): number {
  const list = bank[difficulty];
  if (mode === 'daily') {
    return dailySudokuPuzzleIndex(difficulty, list.length, now ?? new Date());
  }
  return Math.floor(Math.random() * list.length);
}

// ---------------------------------------------------------------------------
// Board helpers
// ---------------------------------------------------------------------------

function freshBoard(puzzle: SudokuPuzzle): SudokuCell[] {
  return puzzle.givens.map((v) => ({
    value: v,
    given: v !== 0,
    candidates: new Set<SudokuDigit>(),
    conflict: false,
    lastErasedAt: 0,
    tiltDeg: 0,
  }));
}

function recomputeConflicts(board: SudokuCell[]): void {
  for (const c of board) c.conflict = false;
  for (let i = 0; i < SIZE; i += 1) {
    const v = board[i]!.value;
    if (v === 0) continue;
    for (const p of peers(i)) {
      if (board[p]!.value === v) {
        board[i]!.conflict = true;
        board[p]!.conflict = true;
      }
    }
  }
}

function computeCounts(
  board: SudokuCell[],
  solution: ReadonlyArray<SudokuDigit>,
): { correctCount: number; emptyCount: number } {
  let correct = 0;
  let empty = 0;
  for (let i = 0; i < SIZE; i += 1) {
    const v = board[i]!.value;
    if (v === 0) {
      empty += 1;
    } else if (v === solution[i]) {
      correct += 1;
    }
  }
  return { correctCount: correct, emptyCount: empty };
}

function isSolved(board: SudokuCell[], solution: ReadonlyArray<SudokuDigit>): boolean {
  for (let i = 0; i < SIZE; i += 1) {
    if (board[i]!.value !== solution[i]) return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// React hook — useSudokuEngine
// ---------------------------------------------------------------------------

export function useSudokuEngine(config: SudokuEngineConfig): SudokuEngineHandle {
  const {
    session,
    difficulty: difficultyEngine,
    initialDifficulty = 'easy',
    mode = 'daily',
    now,
  } = config;

  const bank = useMemo(() => decodedBank(), []);
  const [difficulty, setDifficulty] = useState<SudokuDifficulty>(initialDifficulty);
  const [phase, setPhase] = useState<SudokuPhase>('intro');
  const [puzzleIndex, setPuzzleIndex] = useState(() =>
    pickInitialPuzzleIndex(bank, initialDifficulty, mode, now),
  );

  const puzzle = useMemo(
    () => bank[difficulty][puzzleIndex % bank[difficulty].length]!,
    [bank, difficulty, puzzleIndex],
  );

  const [board, setBoard] = useState<SudokuCell[]>(() => freshBoard(puzzle));
  const [selected, setSelected] = useState<number | null>(null);
  const [pencilMode, setPencilMode] = useState(false);
  const [history, setHistory] = useState<Move[]>([]);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [hintsUsed, setHintsUsed] = useState(0);
  const [mistakeCount, setMistakeCount] = useState(0);

  const started = useRef(false);
  const startedAt = useRef<number | null>(null);
  const difficultyLogged = useRef(false);
  const tickRef = useRef<number | null>(null);

  // Reset board whenever the puzzle reference changes.
  useEffect(() => {
    setBoard(freshBoard(puzzle));
    setSelected(null);
    setHistory([]);
    setHintsUsed(0);
    setMistakeCount(0);
  }, [puzzle]);

  // Timer.
  useEffect(() => {
    if (phase !== 'play') return;
    startedAt.current = startedAt.current ?? performance.now();
    const tick = (): void => {
      const start = startedAt.current;
      if (start === null) return;
      setElapsedSec(Math.floor((performance.now() - start) / 1000));
    };
    tickRef.current = window.setInterval(tick, 500);
    return () => {
      if (tickRef.current !== null) window.clearInterval(tickRef.current);
      tickRef.current = null;
    };
  }, [phase]);

  // Difficulty snapshot — emit once after first start.
  useEffect(() => {
    if (difficultyLogged.current) return;
    if (!started.current) return;
    difficultyLogged.current = true;
    const snap = difficultyEngine.snapshot();
    void session.emit('difficulty_snapshot', {
      axes: snap.axes,
      successRate: snap.successRate,
      resultCount: snap.resultCount,
    });
  }, [phase, difficultyEngine, session]);

  const start = useCallback(
    (chosen?: SudokuDifficulty): void => {
      if (!started.current) {
        started.current = true;
        void session.start();
      }
      if (chosen && chosen !== difficulty) {
        setDifficulty(chosen);
        setPuzzleIndex(pickInitialPuzzleIndex(bank, chosen, mode, now));
      }
      startedAt.current = performance.now();
      setElapsedSec(0);
      setPhase('play');
    },
    [session, difficulty, bank, mode, now],
  );

  const selectCell = useCallback((index: number | null): void => {
    setSelected(index);
  }, []);

  const togglePencilMode = useCallback((): void => {
    setPencilMode((p) => !p);
  }, []);

  /** Make a slightly randomized per-placement tilt (-2.2°..+2.2°). */
  function randomTilt(): number {
    return (Math.random() - 0.5) * 4.4;
  }

  const placeDigit = useCallback(
    (digit: SudokuDigit): void => {
      if (phase !== 'play') return;
      if (selected === null) return;

      setBoard((prev) => {
        const cell = prev[selected];
        if (!cell || cell.given) return prev;
        const next = prev.slice();

        if (pencilMode) {
          // Toggle candidate.
          const newCandidates = new Set(cell.candidates);
          const kind: MoveKind = newCandidates.has(digit) ? 'candidate-remove' : 'candidate-add';
          if (newCandidates.has(digit)) newCandidates.delete(digit);
          else newCandidates.add(digit);
          // Only allow candidates on empty cells.
          if (cell.value !== 0) return prev;
          next[selected] = { ...cell, candidates: newCandidates };
          setHistory((h) => [...h, { kind, index: selected, digit }]);
          return next;
        }

        // Place digit.
        const priorValue = cell.value;
        const priorCandidates = cell.candidates;
        next[selected] = {
          ...cell,
          value: digit,
          candidates: new Set<SudokuDigit>(),
          tiltDeg: randomTilt(),
          lastErasedAt: 0,
        };
        // Remove this digit from peer candidates (cosmetic helper).
        for (const p of peers(selected)) {
          const peer = next[p]!;
          if (peer.candidates.has(digit)) {
            const newSet = new Set(peer.candidates);
            newSet.delete(digit);
            next[p] = { ...peer, candidates: newSet };
          }
        }
        recomputeConflicts(next);

        const placedConflict = next[selected]!.conflict;
        if (placedConflict) setMistakeCount((m) => m + 1);

        const move: Move = {
          kind: 'place',
          index: selected,
          digit,
          priorCandidates,
        };
        if (priorValue !== 0) move.priorValue = priorValue as SudokuDigit;
        setHistory((h) => [...h, move]);

        // Telemetry — a single move event with correctness vs solution.
        const correct = puzzle.solution[selected] === digit;
        void session.emit(correct ? 'correct' : 'incorrect', {
          index: selected,
          digit,
          conflict: placedConflict,
        });

        return next;
      });
    },
    [phase, selected, pencilMode, puzzle.solution, session],
  );

  const erase = useCallback((): void => {
    if (phase !== 'play') return;
    if (selected === null) return;
    setBoard((prev) => {
      const cell = prev[selected];
      if (!cell || cell.given) return prev;
      if (cell.value === 0 && cell.candidates.size === 0) return prev;
      const next = prev.slice();
      const priorValue = cell.value;
      const priorCandidates = cell.candidates;
      next[selected] = {
        ...cell,
        value: 0,
        candidates: new Set<SudokuDigit>(),
        lastErasedAt: performance.now(),
        tiltDeg: 0,
      };
      recomputeConflicts(next);
      if (priorValue !== 0) {
        setHistory((h) => [
          ...h,
          {
            kind: 'erase',
            index: selected,
            digit: priorValue as SudokuDigit,
            priorCandidates,
          },
        ]);
      }
      return next;
    });
  }, [phase, selected]);

  const undo = useCallback((): void => {
    setHistory((h) => {
      if (h.length === 0) return h;
      const last = h[h.length - 1]!;
      setBoard((prev) => {
        const next = prev.slice();
        const cell = next[last.index]!;
        if (last.kind === 'place') {
          next[last.index] = {
            ...cell,
            value: last.priorValue ?? 0,
            candidates: new Set(last.priorCandidates ?? []),
            lastErasedAt: 0,
          };
          recomputeConflicts(next);
        } else if (last.kind === 'erase') {
          next[last.index] = {
            ...cell,
            value: last.digit,
            candidates: new Set<SudokuDigit>(),
            lastErasedAt: 0,
            tiltDeg: randomTilt(),
          };
          recomputeConflicts(next);
        } else if (last.kind === 'candidate-add') {
          const c = new Set(cell.candidates);
          c.delete(last.digit);
          next[last.index] = { ...cell, candidates: c };
        } else if (last.kind === 'candidate-remove') {
          const c = new Set(cell.candidates);
          c.add(last.digit);
          next[last.index] = { ...cell, candidates: c };
        }
        return next;
      });
      return h.slice(0, -1);
    });
  }, []);

  const hint = useCallback((): void => {
    if (phase !== 'play') return;
    setBoard((prev) => {
      // Pick an empty/wrong cell at random, fill with solution.
      const candidates: number[] = [];
      for (let i = 0; i < SIZE; i += 1) {
        const c = prev[i]!;
        if (c.given) continue;
        if (c.value !== puzzle.solution[i]) candidates.push(i);
      }
      if (candidates.length === 0) return prev;
      const target = candidates[Math.floor(Math.random() * candidates.length)]!;
      const next = prev.slice();
      next[target] = {
        ...next[target]!,
        value: puzzle.solution[target]!,
        candidates: new Set<SudokuDigit>(),
        tiltDeg: randomTilt(),
        lastErasedAt: 0,
      };
      recomputeConflicts(next);
      setHintsUsed((n) => n + 1);
      return next;
    });
  }, [phase, puzzle.solution]);

  const resetPuzzle = useCallback((): void => {
    setBoard(freshBoard(puzzle));
    setSelected(null);
    setHistory([]);
    setMistakeCount(0);
    setHintsUsed(0);
    startedAt.current = performance.now();
    setElapsedSec(0);
  }, [puzzle]);

  const nextPuzzle = useCallback(
    (modeOverride?: SudokuMode): void => {
      const nextMode = modeOverride ?? mode;
      setPuzzleIndex((current) => {
        const listLength = bank[difficulty].length;
        if (nextMode === 'daily') {
          return dailySudokuPuzzleIndex(difficulty, listLength, now ?? new Date());
        }
        if (listLength <= 1) return 0;
        const next = pickInitialPuzzleIndex(bank, difficulty, 'practice', now);
        return next === current ? (next + 1) % listLength : next;
      });
      startedAt.current = performance.now();
      setElapsedSec(0);
      setPhase('play');
    },
    [bank, difficulty, mode, now],
  );

  const pickDifficulty = useCallback(
    (chosen: SudokuDifficulty): void => {
      if (chosen === difficulty) return;
      setDifficulty(chosen);
      setPuzzleIndex(pickInitialPuzzleIndex(bank, chosen, mode, now));
      startedAt.current = performance.now();
      setElapsedSec(0);
    },
    [difficulty, bank, mode, now],
  );

  const solveNow = useCallback((): void => {
    setBoard((prev) => {
      const next = prev.slice();
      for (let i = 0; i < SIZE; i += 1) {
        const cell = next[i]!;
        next[i] = {
          ...cell,
          value: puzzle.solution[i]!,
          candidates: new Set<SudokuDigit>(),
          lastErasedAt: 0,
          tiltDeg: cell.given ? 0 : (Math.random() - 0.5) * 4.4,
          conflict: false,
        };
      }
      return next;
    });
  }, [puzzle.solution]);

  const end = useCallback(async (): Promise<void> => {
    const { correctCount, emptyCount } = computeCounts(board, puzzle.solution);
    const totalNonGiven = SIZE - puzzle.givens.filter((v) => v !== 0).length;
    const accuracy = totalNonGiven > 0 ? correctCount / totalNonGiven : 1;
    const completed = emptyCount === 0 && isSolved(board, puzzle.solution);
    await session.end({
      finalScore: Math.max(0, correctCount * 10 - mistakeCount * 5 - hintsUsed * 8),
      accuracy,
      completed,
    });
  }, [board, puzzle, mistakeCount, hintsUsed, session]);

  // Solved detection — derived; flips phase when board matches solution.
  const counts = useMemo(() => computeCounts(board, puzzle.solution), [board, puzzle.solution]);

  const justSolved = useRef(false);
  useEffect(() => {
    if (phase !== 'play') return;
    if (counts.emptyCount > 0) return;
    if (!isSolved(board, puzzle.solution)) return;
    if (justSolved.current) return;
    justSolved.current = true;
    setPhase('solved');
    // Stop the timer.
    if (tickRef.current !== null) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
    const delay = prefersReducedMotion() ? 0 : 60;
    window.setTimeout(() => {
      void session.emit('puzzle_solved', {
        elapsedSec,
        hintsUsed,
        mistakeCount,
        difficulty,
      });
    }, delay);
  }, [
    phase,
    board,
    counts.emptyCount,
    puzzle.solution,
    elapsedSec,
    hintsUsed,
    mistakeCount,
    difficulty,
    session,
  ]);

  // Re-arm `justSolved` when we transition out of solved (next puzzle).
  useEffect(() => {
    if (phase !== 'solved') justSolved.current = false;
  }, [phase]);

  const state: SudokuEngineState = {
    phase,
    difficulty,
    puzzle,
    board,
    selected,
    pencilMode,
    undoAvailable: history.length > 0,
    correctCount: counts.correctCount,
    emptyCount: counts.emptyCount,
    elapsedSec,
    hintsUsed,
    mistakeCount,
  };

  const actions: SudokuEngineActions = {
    start,
    selectCell,
    togglePencilMode,
    placeDigit,
    erase,
    undo,
    hint,
    resetPuzzle,
    nextPuzzle,
    pickDifficulty,
    solveNow,
    end,
  };

  // Dev hook for screenshots / e2e — only in dev mode.
  if (typeof window !== 'undefined' && import.meta.env.DEV) {
    (window as unknown as { __sudokuActions?: SudokuEngineActions }).__sudokuActions = actions;
  }

  return { state, actions };
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

export function formatElapsed(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function difficultyLabel(difficulty: SudokuDifficulty): string {
  switch (difficulty) {
    case 'easy':
      return 'Easy';
    case 'medium':
      return 'Medium';
    case 'hard':
      return 'Hard';
    case 'expert':
      return 'Expert';
  }
}
